import React, {useEffect, useRef, useState} from 'react';
import {PushNotifications} from '@capacitor/push-notifications';
import {FCM} from '@capacitor-community/fcm';
import {Device} from '@capacitor/device';
import {Capacitor} from "@capacitor/core";
import type {PluginListenerHandle} from "@capacitor/core";
import {useAuthStore} from "../../redux/auth";
import {useToastStore} from "../../redux/toast";
import {useCustomerStore} from "../../redux/customers";
import {useHistory} from "react-router";
import {useIonRouter} from "@ionic/react";
import {useChatsStore} from "../../redux/chats";
import {handleNotificationAction} from "../common/pushListenerHandler";
import {handleDeepLinkUrl} from "../common/deepLinkHandler";
import {handleReminderForeground} from "../common/reminderForegroundHandler";

// A handled chat deep link short-circuits the tap path before
// handleNotificationAction runs, so the pushed message itself must still be
// ingested or the thread renders stale (the store dedupes by id, and messages
// carry their own customerId, so this is safe for any customer).
const ingestChatMessage = (data: any, addMessage: (message: any) => void) => {
    if (data?.type !== 'chatMessage' || !data.chatMessage) return;
    try {
        const parsedMessage = JSON.parse(data.chatMessage);
        if (parsedMessage?.customerId) {
            addMessage(parsedMessage);
        }
    } catch (error) {
        console.error('Failed to parse chatMessage from notification tap:', error);
    }
};

const PushListener: React.FC = () => {
    const isPushNotificationsAvailable = Capacitor.isPluginAvailable('PushNotifications');
    const isDeviceAvailable = Capacitor.isPluginAvailable('Device');
    const history = useHistory();
    const ionRouter = useIonRouter();
    const {showError, show: showToast} = useToastStore();
    const {registerFcmToken} = useAuthStore();
    const [deviceUuid, setDeviceUuid] = useState<string>();
    const [devicePlatform, setDevicePlatform] = useState<string>();
    const [deviceFcmToken, setDeviceFcmToken] = useState<string>();
    const [registered, setRegistered] = useState<boolean>(false);

    const customerStore = useCustomerStore();
    const {setSelectedId: setSelectedCustomerId, customers, activeCustomer} = customerStore;
    const chatsStore = useChatsStore();
    const {addMessage} = chatsStore;

    // Use refs for dynamic state to avoid closure staleness in listeners
    const customersRef = useRef(customers);
    const activeCustomerRef = useRef(activeCustomer);
    const setSelectedCustomerIdRef = useRef(setSelectedCustomerId);
    const addMessageRef = useRef(addMessage);
    const historyRef = useRef(history);
    const showToastRef = useRef(showToast);

    const [pendingNotification, setPendingNotification] = useState<any | null>(null);

    useEffect(() => {
        customersRef.current = customers;
    }, [customers]);

    useEffect(() => {
        activeCustomerRef.current = activeCustomer;
    }, [activeCustomer]);

    useEffect(() => {
        setSelectedCustomerIdRef.current = setSelectedCustomerId;
    }, [setSelectedCustomerId]);

    useEffect(() => {
        addMessageRef.current = addMessage;
    }, [addMessage]);

    useEffect(() => {
        historyRef.current = history;
    }, [history]);

    useEffect(() => {
        showToastRef.current = showToast;
    }, [showToast]);

    // Handle processing pending notification once customers are available
    useEffect(() => {
        if (pendingNotification && customers && customers.length > 0) {
            const data = pendingNotification;
            setPendingNotification(null);
            console.log('Processing pending notification action with loaded customers:', data);

            (async () => {
                const currentLocation = historyRef.current?.location?.pathname;
                try {
                    if (data.deepLinkUrl) {
                        const handled = await handleDeepLinkUrl({
                            url: data.deepLinkUrl,
                            customers: customersRef.current,
                            setSelectedCustomerId: setSelectedCustomerIdRef.current,
                            history: historyRef.current,
                            ionRouter,
                            currentLocation,
                        });

                        if (handled) {
                            ingestChatMessage(data, addMessageRef.current);
                            return;
                        }
                    }

                    await handleNotificationAction({
                        data,
                        customers: customersRef.current,
                        setSelectedCustomerId: setSelectedCustomerIdRef.current,
                        history: historyRef.current,
                        ionRouter,
                        currentLocation,
                        addMessage: addMessageRef.current,
                    });
                } catch (error) {
                    console.error('Failed to process pending notification action:', error, data);
                }
            })();
        }
    }, [pendingNotification, customers]);

    // Collects the plugin-global listener handles so they can be remove()d on
    // unmount. `handles` is filled as each listener resolves, so a cleanup that
    // fires mid-registration still removes whatever has already been added.
    const addListeners = async (handles: PluginListenerHandle[]): Promise<PluginListenerHandle[]> => {
        handles.push(await PushNotifications.addListener('registrationError', (err) => {
            console.error('Registration error:', err);
            showError({message: 'Error while registering notifications', duration: 2000});
        }));

        handles.push(await PushNotifications.addListener('pushNotificationReceived', (notification) => {
            console.log('Push notification received:', notification);
            if (!notification?.data?.type) {
                console.warn('Push notification received without a type key:', notification.data);
                return;
            }

            const {type} = notification.data;

            try {
                switch (type) {
                    case 'chatMessage': {
                        const {chatMessage} = notification.data;
                        if (chatMessage) {
                            const parsedMessage = JSON.parse(chatMessage);
                            console.log('Parsed ChatMessage:', parsedMessage);
                            if (parsedMessage.customerId) {
                                handleIncomingMessage(parsedMessage);
                            }
                        }
                        break;
                    }
                    case 'shopPaymentComplete':
                        console.log('Shop payment complete notification received in foreground');
                        // For now just log it, we might want to show a toast or refresh some state
                        break;
                    case 'eventReminder': {
                        const data = notification.data;
                        const eventId = data.eventId;
                        const customerId = data.customerId
                            ? parseInt(data.customerId as string, 10)
                            : null;
                        const knownCustomer = customerId != null
                            && customersRef.current?.some((c) => c.id === customerId);

                        const navigate = eventId
                            ? async () => {
                                if (knownCustomer && activeCustomerRef.current?.id !== customerId) {
                                    await setSelectedCustomerIdRef.current({id: customerId as number});
                                }
                                historyRef.current.push(`/events/${eventId}`);
                            }
                            : null;

                        handleReminderForeground({
                            notification: {
                                title: notification.title,
                                body: notification.body,
                            },
                            showToast: showToastRef.current,
                            navigate,
                        }).catch((err) => console.error('Reminder foreground toast failed:', err));
                        break;
                    }
                    case 'bookingReminder': {
                        handleReminderForeground({
                            notification: {
                                title: notification.title,
                                body: notification.body,
                            },
                            showToast: showToastRef.current,
                            navigate: null,
                        }).catch((err) => console.error('Reminder foreground toast failed:', err));
                        break;
                    }
                    default:
                        console.warn('Unknown notification type received:', type);
                }
            } catch (error) {
                console.error('Failed to process push notification:', error, notification.data);
            }
        }));

        handles.push(await PushNotifications.addListener('pushNotificationActionPerformed', async (notificationAction) => {
            const data = notificationAction?.notification?.data;
            if (!data) return;

            console.log('Push notification action performed:', data);

            // Use the latest customer state from ref
            if (!customersRef.current || customersRef.current.length === 0) {
                console.log('Push notification action received but customers not yet loaded. Queuing.');
                setPendingNotification(data);
                return;
            }

            const currentLocation = historyRef.current?.location?.pathname;

            try {
                if (data.deepLinkUrl) {
                    const handled = await handleDeepLinkUrl({
                        url: data.deepLinkUrl,
                        customers: customersRef.current,
                        setSelectedCustomerId: setSelectedCustomerIdRef.current,
                        history: historyRef.current,
                        ionRouter,
                        currentLocation,
                    });

                    if (handled) {
                        ingestChatMessage(data, addMessageRef.current);
                        return;
                    }
                }

                await handleNotificationAction({
                    data,
                    customers: customersRef.current,
                    setSelectedCustomerId: setSelectedCustomerIdRef.current,
                    history: historyRef.current,
                    ionRouter,
                    currentLocation,
                    addMessage: addMessageRef.current,
                });
            } catch (error) {
                console.error('Failed to process push notification action:', error, data);
            }
        }));

        return handles;
    };

    const handleIncomingMessage = (chatMessage: any) => {
        console.log(`New message received for customer ${chatMessage.customerId}:`, chatMessage);
        if (activeCustomerRef.current?.id === chatMessage.customerId) {
            addMessageRef.current(chatMessage);
        } else {
            console.warn('Message received but customer is not active. Skipping auto-update.');
        }
    };

    const registerNotifications = async () => {
        if (isPushNotificationsAvailable) {
            let permStatus = await PushNotifications.checkPermissions();
            if (permStatus.receive === 'prompt') {
                permStatus = await PushNotifications.requestPermissions();
            }
            if (permStatus.receive === 'granted') {
                await PushNotifications.register();
                try {
                    const {token} = await FCM.getToken();
                    setDeviceFcmToken(token);
                } catch (error) {
                    console.error('Error getting FCM token:', error);
                    showError({message: 'Error while registering notifications', duration: 2000});
                }
            }
        }
    };

    // Capacitor push listeners are plugin-global. Without cleanup they accumulate
    // a full extra set on every logout→login remount, and the zombie sets fire
    // with frozen refs. Keep the handles and remove() them on unmount, as
    // DeepLinkListener does. Keyed only on availability so it survives the
    // `registered` flips that drive the registration effect below.
    useEffect(() => {
        if (!isPushNotificationsAvailable) return;

        const handles: PluginListenerHandle[] = [];
        let cancelled = false;

        addListeners(handles)
            .then(() => {
                if (cancelled) {
                    handles.forEach((h) => h.remove().catch(() => {}));
                }
            })
            .catch((error) => console.error('Failed to add push notification listeners:', error));

        return () => {
            cancelled = true;
            handles.forEach((h) => h.remove().catch(
                (error) => console.error('Failed to remove push notification listener:', error)
            ));
        };
        // Register once and tear down on unmount; addListeners closes over stable
        // refs, so re-subscribing on its identity would needlessly churn listeners.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isPushNotificationsAvailable]);

    useEffect(() => {
        if (!registered && isPushNotificationsAvailable && isDeviceAvailable) {
            Promise.all([Device.getInfo(), Device.getId()])
                .then(([deviceInfo, deviceId]) => {
                    setDevicePlatform(deviceInfo.platform);
                    setDeviceUuid(deviceId.identifier);
                    if (deviceInfo.platform === 'ios' || deviceInfo.platform === 'android') {
                        registerNotifications().catch(() => showError({
                            message: 'Error while registering notifications',
                            duration: 2000
                        }));
                    }
                })
                .catch(() => showError({message: 'Error while registering notifications', duration: 2000}));
        }
    }, [registered, isPushNotificationsAvailable, isDeviceAvailable]);

    useEffect(() => {
        if (!registered && deviceFcmToken && devicePlatform && deviceUuid) {
            registerFcmToken({deviceUuid, fcmToken: deviceFcmToken, platform: devicePlatform, customerId: activeCustomer?.id}).then(() => {
                setRegistered(true);
            });
        }
    }, [registered, deviceFcmToken, devicePlatform, deviceUuid, activeCustomer?.id, registerFcmToken]);

    return null;
};

export default PushListener;
